import { useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { StandardLayout } from '@factorialco/f0-react'
import {
  EmployeesPersonalDataDocument,
  ResourceFieldDataFragment,
  ValueDataFragment,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'
import { observable } from 'mobx'

import { observer } from 'mobx-react'

import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import { WhatsAppButton } from 'modules/core/components/WhatsAppButton'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'
import { employeeUser } from 'modules/core/store/collections/users'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { ContractCuesProvider } from 'modules/contracts/lib/contractCuesContext'
import ContractCuesBanner from 'modules/contracts/pages/ContractVersionsPage/Id/ContractCuesBanner'
import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import { filterCustomResourceFieldsDataByUsageGroup } from 'modules/custom_resources/lib/utils'
import Actions from 'modules/employees/components/Actions'
import LoadingState from 'modules/employees/components/Personal/LoadingState'
import PayrollBlock from 'modules/employees/components/Personal/_payroll'
import BankInfo from 'modules/employees/components/blocks/BankInfo'
import Contact from 'modules/employees/components/blocks/Contact'
import EmployeeAddress from 'modules/employees/components/blocks/EmployeeAddress'
import EmployeeIdentification from 'modules/employees/components/blocks/EmployeeIdentification'
import { EmployeePersonalProvider } from 'modules/employees/components/blocks/EmployeePersonalProvider'
import EmployeeRetirementRegimeApplied from 'modules/employees/components/blocks/EmployeeRetirementRegime'
import EmployeeStatusBanner from 'modules/employees/components/blocks/EmployeeStatusBanner'
import FamilySituation from 'modules/employees/components/blocks/FamilySituation'
import General from 'modules/employees/components/blocks/General'
import Irpf from 'modules/employees/components/blocks/Irpf'
import { PayrollEmployeeData } from 'modules/employees/components/blocks/PayrollEmployee'
import { getPayrollEmployeeCategories } from 'modules/employees/components/blocks/PayrollEmployee/categories'
import PersonalSituation from 'modules/employees/components/blocks/PersonalSituation'
import TaxResidence from 'modules/employees/components/blocks/TaxResidence'
import type { CommonRouteContext } from 'modules/employees/lib/types'
import PrimoboxStatusRow from 'modules/integrations_primobox/components/StatusRow'
import Header from 'modules/layout/components/Page/Header'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface Props {
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
}

const Personal = ({ customResourceFieldsData, customValuesData }: Props) => {
  const context = useOutletContext<CommonRouteContext>()
  const {
    loadingOnboardingProcess,
    cardEmployeeRight,
    headerTitle,
    employee,
    canSeePersonalInfo,
    canEditPersonalInfo,
  } = context

  const [legalEntity, setLegalEntity] = useState<LegalEntity | null>(null)
  const readonly = !canEditPersonalInfo

  const employeeId = employee.attributes.id
  const listOfFields = useRef(observable.map(new Map())).current

  useAsyncData(async () => {
    const employeeLegalEntity: LegalEntity[] = await legalEntities.fetch({
      data: {
        employee_id: employee.id,
      },
      remove: false, // Don't remove the other LEs from the global collection
    })

    if (Array.isArray(employeeLegalEntity)) {
      setLegalEntity(employeeLegalEntity[0] || null)
    }
  }, [employee.attributes.id])

  const { employee: currentEmployee, company } = useCurrentSession()

  const { data: employeeDocument, loading: loadingEmployeeData } = useQuery(
    EmployeesPersonalDataDocument,
    {
      variables: {
        companyId: company.id,
        employeeId: employeeId,
        country: legalEntity?.country || '',
      },
    }
  )

  if (loadingEmployeeData) {
    return (
      <StandardLayout variant="narrow">
        <LoadingState />
      </StandardLayout>
    )
  }

  const employeeData = employeeDocument?.employees.employeesConnection.edges[0]?.node

  if (!employeeData) return null

  const features = employeeDocument?.features.featuresConnection.nodes ?? []
  const employeePayrollDataSchema =
    employeeDocument?.dataDispatcher.dataSchemasConnection.nodes ?? []
  const employeePayrollData = employeeDocument?.payrollEmployees.employeeDataConnection.nodes ?? []
  const employeePayrollDatum =
    employeePayrollData.find((data) => data.employeeId === employeeId)?.fields ?? []
  const groupedSchemaFields = groupBy(employeePayrollDataSchema, 'category')

  const hiringIncidences =
    employeeDocument?.bookkeepersManagement?.incidencesConnection?.nodes ?? []
  const hasHiringIncidence = hiringIncidences.length > 0
  const incidenceRules = hasHiringIncidence
    ? employeeDocument?.bookkeepersManagement?.incidenceRulesConnection?.nodes ?? []
    : []

  const payrollable = employeeData.payrollable ?? false

  const onboardingProcess =
    employeeDocument?.employeeOnboarding?.employeeOnboardingsConnection?.nodes[0]
      ?.materializedProcess?.process

  const hasFeature = (featureName: string) => {
    return features.find((feature) => feature.name === featureName)?.enabled ?? false
  }

  const hasPrimobox = hasFeature('integrations_primobox_active')
  const hasWhatsappFeature = hasFeature('employee_chat_on_whatsapp')
  const hasPayrollBundle = hasFeature('payroll_bundle')
  const primoboxEmployeeStatus =
    employeeDocument?.integrationsPrimobox?.employeeStatusesConnection?.nodes[0]

  const hasCues = (!readonly && incidenceRules?.length > 0) ?? false

  const belongsToGermanLegalEntity = legalEntity?.country === 'de'
  const belongsToMexicanLegalEntity = legalEntity?.country === 'mx'
  const belongsToSpanishLegalEntity = legalEntity?.country === 'es'
  return (
    <ResponsivePage.Body
      variant="standard"
      loading={loadingEmployeeData}
      error={!employeeData}
      height="full"
    >
      <StandardLayout variant="narrow">
        <ContractCuesProvider
          collection={{
            hasCues: hasCues,
            incidenceRules: incidenceRules,
            listOfFields: listOfFields,
          }}
        >
          <EmployeePersonalProvider employee={employee}>
            <PortalWrapper container={cardEmployeeRight}>
              <WhatsAppButton
                employee={employeeData}
                canSeePersonalInfo={canSeePersonalInfo}
                hasWhatsappFeature={hasWhatsappFeature}
              />
              <Actions employeeData={employeeData} />
            </PortalWrapper>
            <PortalWrapper container={headerTitle}>
              <Header.Title>{i18n.t('profile.personal_details')}</Header.Title>
            </PortalWrapper>
            {hasCues && (
              <ContractCuesBanner
                title={i18n.t('employee_form.cues_banner.title', {
                  employeeName: employeeUser(employee).firstName,
                })}
                employeeName={employeeUser(employee).firstName}
              ></ContractCuesBanner>
            )}
            {!loadingOnboardingProcess && employeeData && (
              <EmployeeStatusBanner
                access={employeeData.access}
                contractStartDate={employeeData.referenceContract?.startsOn}
                employee={employeeData}
                policies={employeeData._policies}
                onboardingProcessName={onboardingProcess?.name}
              />
            )}
            {employeeData && (
              <General
                employee={employeeData}
                isCurrent={currentEmployee.id === employeeData.id}
                readonly={readonly}
              />
            )}
            {belongsToGermanLegalEntity && (
              <PayrollEmployeeData
                employeeId={employeeId}
                companyId={company.id}
                employeePayrollDataSchema={groupedSchemaFields['general_information'] || []}
                employeePayrollDatum={employeePayrollDatum}
                illustration="user-male-female"
                title={i18n.t('employee_form.payroll_data.categories.general_information.title')}
                description={i18n.t(
                  'employee_form.payroll_data.categories.general_information.description'
                )}
              />
            )}
            <EmployeeIdentification
              employee={employeeData}
              readonly={readonly}
              payroll={payrollable}
              isCurrent={currentEmployee.id === employeeData.id}
            />
            <EmployeeRetirementRegimeApplied employeeId={employee.id} readonly={readonly} />
            <EmployeeAddress
              employee={employeeData}
              readonly={readonly}
              isCurrent={currentEmployee.id === employeeData.id}
            />
            <PersonalSituation employee={employeeData} readonly={readonly} />
            <FamilySituation employee={employeeData} readonly={readonly} />
            {payrollable && belongsToSpanishLegalEntity && (
              <Irpf employee={employeeData} isCurrent={currentEmployee.id === employeeData.id} />
            )}
            <TaxResidence
              hasPayrollBundle={hasPayrollBundle}
              employee={employeeData}
              isCurrent={currentEmployee.id === employeeData.id}
            />
            <Contact
              employee={employeeData}
              readonly={readonly}
              isCurrent={currentEmployee.id === employeeData.id}
            />
            <BankInfo
              employee={employeeData}
              canEditPersonalInfo={canEditPersonalInfo}
              canSeeBankInfo={canSeePersonalInfo}
            />
            {belongsToGermanLegalEntity &&
              getPayrollEmployeeCategories().map((category) => (
                <PayrollEmployeeData
                  key={`${category.name}-employee-data`}
                  employeeId={employeeId}
                  companyId={company.id}
                  employeePayrollDataSchema={groupedSchemaFields[category.name] || []}
                  employeePayrollDatum={employeePayrollDatum}
                  illustration={category.illustration}
                  title={category.title}
                  description={category.description}
                />
              ))}
            {hasFeature('display_new_payroll') && legalEntity && (
              <>
                <PayrollBlock
                  employee={employee}
                  readonly={readonly}
                  customResourceFieldsData={filterCustomResourceFieldsDataByUsageGroup(
                    customResourceFieldsData,
                    'general-information'
                  )}
                  customValuesData={customValuesData}
                  employeeCanEditPersonalInfo={canEditPersonalInfo}
                />
                {hasPrimobox && primoboxEmployeeStatus && (
                  <PrimoboxStatusRow
                    cardTitle={i18n.t('integrations_primobox.status_card.employee.title')}
                    status={primoboxEmployeeStatus}
                  />
                )}
                <CustomFieldsForm
                  boxed
                  customResourceFieldsData={filterCustomResourceFieldsDataByUsageGroup(
                    customResourceFieldsData,
                    'basic-information'
                  )}
                  customValuesData={customValuesData}
                  description={i18n.t('employee_form.custom_fields.basic_information.description')}
                  icon="building"
                  padFormDirection="horizontal"
                  readonly={readonly}
                  title={i18n.t('employee_form.custom_fields.basic_information.title')}
                  valuableId={employee.id}
                  valuableType="Employee"
                  currency={employee?.attributes.legal_entity_currency}
                />
                {belongsToMexicanLegalEntity && (
                  <CustomFieldsForm
                    boxed
                    customResourceFieldsData={filterCustomResourceFieldsDataByUsageGroup(
                      customResourceFieldsData,
                      'identification-number'
                    )}
                    customValuesData={customValuesData}
                    description={i18n.t(
                      'employee_form.custom_fields.identification_number.description'
                    )}
                    icon="building"
                    padFormDirection="horizontal"
                    readonly={readonly}
                    title={i18n.t('employee_form.custom_fields.identification_number.title')}
                    valuableId={employee.id}
                    valuableType="Employee"
                    currency={employee?.attributes.legal_entity_currency}
                  />
                )}
              </>
            )}
          </EmployeePersonalProvider>
        </ContractCuesProvider>
      </StandardLayout>
    </ResponsivePage.Body>
  )
}

export default observer(Personal)
